import { useState, useEffect, useContext, useCallback } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";

export function useUnsavedChanges(isDirty, onSave) {
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [pendingTransition, setPendingTransition] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const navigationContext = useContext(UNSAFE_NavigationContext);
    const navigator = navigationContext?.navigator;

    const performWithConfirmation = useCallback((actionCallback) => {
        if (isDirty) {
            setPendingAction(() => actionCallback);
            setShowUnsavedModal(true);
        } else {
            actionCallback();
        }
    }, [isDirty]);

    // Trigger browser warning dialog on page refresh (F5) or tab close when form is dirty
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return 'You have unsaved changes. Are you sure you want to leave?';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // Intercept React Router v6 navigation (settings tabs, sidebar links, route changes) when form is dirty
    useEffect(() => {
        if (!isDirty || !navigator) return;

        const originalPush = navigator.push;
        const originalReplace = navigator.replace;

        navigator.push = (...args) => {
            setPendingTransition({
                retry: () => originalPush.apply(navigator, args)
            });
            setShowUnsavedModal(true);
        };

        navigator.replace = (...args) => {
            setPendingTransition({
                retry: () => originalReplace.apply(navigator, args)
            });
            setShowUnsavedModal(true);
        };

        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [isDirty, navigator]);

    const handleCancel = () => {
        setShowUnsavedModal(false);
        setPendingTransition(null);
        setPendingAction(null);
    };

    const handleDiscard = () => {
        setShowUnsavedModal(false);
        if (pendingTransition && typeof pendingTransition.retry === 'function') {
            pendingTransition.retry();
        }
        if (pendingAction) {
            pendingAction();
        }
        setPendingTransition(null);
        setPendingAction(null);
    };

    /**
     * Handles save and exit action during unsaved changes modal confirmation.
     * Aborts route transition and keeps user on current view if validation/save fails.
     * @returns {Promise<void>}
     */
    const handleSaveAndExit = async () => {
        try {
            const success = await onSave();
            if (success === false) {
                // Validation error or save failure - stop navigation & stay on current page
                setShowUnsavedModal(false);
                setPendingTransition(null);
                setPendingAction(null);
                return;
            }
            setShowUnsavedModal(false);
            if (pendingTransition && typeof pendingTransition.retry === 'function') {
                pendingTransition.retry();
            }
            if (pendingAction) {
                pendingAction();
            }
            setPendingTransition(null);
            setPendingAction(null);
        } catch (err) {
            console.error("Failed to save on exit:", err);
            setShowUnsavedModal(false);
            setPendingTransition(null);
            setPendingAction(null);
        }
    };

    return {
        showUnsavedModal,
        setShowUnsavedModal,
        performWithConfirmation,
        handleCancel,
        handleDiscard,
        handleSaveAndExit
    };
}
